import { useHistory, useLocation } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '~/contexts/UserContext';
import { PrepApi } from '~/api/prep';
import { Card, CardContent } from '~/shadcn/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/shadcn/components/ui/Tabs';
import SummaryCards from './components/SummaryCards';
import AllLocationsTable from './components/AllLocationsTable';
import PrepInboundTable from './components/PrepInboundTable';
import PrepCompleteTable from './components/PrepCompleteTable';

/**
 * Renders the main container for the Prep Management page, organizing different views into tabs.
 * This component will house the "All Locations," "Prep Inbound," and "Prep Complete" tables.
 *
 * @returns {JSX.Element} The tabbed interface for the Prep page.
 */
function InventoryPrepTabs() {
    const { user } = useContext(UserContext);
    const prepApi = new PrepApi();

    const [summaryData, setSummaryData] = useState({
        totalPallets: 0,
        prepInboundPallets: 0,
        prepCompletePallets: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                setIsLoading(true);
                const response = await prepApi.getPrepInventorySummary({ facilityId: user.facilityID });
                if (response.status) {
                    setSummaryData({
                        totalPallets: response.data.totalPallets,
                        prepInboundPallets: response.data.totalPalletsInbound,
                        prepCompletePallets: response.data.totalPalletsComplete,
                    });
                } else {
                    setError('Failed to fetch summary data.');
                }
            } catch (err) {
                setError(err.message || 'An unexpected error occurred.');
            } finally {
                setIsLoading(false);
            }
        };

        if (user?.facilityID) {
            fetchSummary();
        }
    }, [user?.facilityID]);

    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const activeTab = queryParams.get('tab') || 'all-locations';

    // Todo: use this state to filter the other tables
    const locationFilter = queryParams.get('location');

    const handleViewPallets = ({ locationType, locationName }) => {
        const targetTab = locationType === 'Inbound' ? 'prep-inbound' : 'prep-complete';
        const newParams = new URLSearchParams();
        newParams.set('tab', targetTab);
        newParams.set('location', locationName);
        history.push({ search: newParams.toString() });
    };

    const handleTabChange = (newTab) => {
        const newParams = new URLSearchParams(location.search);
        newParams.set('tab', newTab);
        newParams.delete('location'); // Clear location filter on manual tab change
        history.push({ search: newParams.toString() });
    };

    const tabsData = [
        {
            value: 'all-locations',
            label: 'All Locations',
            content: <AllLocationsTable onViewPallets={handleViewPallets} />,
        },
        {
            value: 'prep-inbound',
            label: 'Prep Inbound',
            content: <PrepInboundTable locationFilter={locationFilter} />,
        },
        {
            value: 'prep-complete',
            label: 'Prep Complete',
            content: <PrepCompleteTable locationFilter={locationFilter} />,
        },
    ];

    return (
        <div className="flex flex-col w-full h-full p-4 md:p-6 bg-gray-50/50">
            <h3 className="text-xl font-bold mb-4 text-left">Prep Operations Dashboard</h3>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <div className="border-b border-gray-300 text-left">
                    <TabsList className="bg-transparent p-0 rounded-none">
                        {tabsData.map((tab) => (
                            <TabsTrigger
                                key={tab.value}
                                value={tab.value}
                                className="data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-b-blue-500 rounded-none data-[state=active]:text-blue-500 px-2.5 py-2 data-[state=active]:bg-transparent"
                            >
                                {tab.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                <SummaryCards
                    totalPallets={summaryData.totalPallets}
                    prepInboundPallets={summaryData.prepInboundPallets}
                    prepCompletePallets={summaryData.prepCompletePallets}
                    isLoading={isLoading}
                    error={error}
                />

                {tabsData.map((tab) => (
                    <TabsContent key={tab.value} value={tab.value} className="mt-1">
                        <Card>
                            <CardContent className="p-6">{tab.content}</CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}

export default InventoryPrepTabs;
